import { useState, useRef, useEffect } from 'react';
import { Popover } from '@c2fo/liquidity';
import {
  AccountSettingIcon,
  GlobeAsiaIcon,
  AccountMultipleIcon,
  AngleDownIcon,
  CheckIcon,
} from '@c2fo/liquidity/icons';
import { useUserDefaults } from '../../hooks';
import { useProfilesQuery } from '../../api/Profiles';
import { DATACENTERS } from '@admin-ui-root/src/app/utils/settings';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full h-9 px-3 text-sm rounded-md border border-stroke bg-white shadow-sm cursor-pointer flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-lightBlue-500 focus:border-lightBlue-500 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption?.label ?? placeholder}
        </span>
        <AngleDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stroke rounded-md shadow-lg max-h-48 overflow-auto z-10">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-sm text-left hover:bg-lightBlue-500/5 flex items-center justify-between"
            >
              <span>{option.label}</span>
              {option.value === value && <CheckIcon className="h-4 w-4 text-lightBlue-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const SettingsMenu: React.FC = () => {
  const { defaults, setDefaultDataRegion, setDefaultServiceAccount } = useUserDefaults();
  const { data: profiles = [], isLoading: isLoadingAccounts } = useProfilesQuery();

  const sortedAccounts = [...profiles].sort((a, b) => {
    const aIsEpadmin = a.fdsServiceAccount.toLowerCase() === 'epadmin';
    const bIsEpadmin = b.fdsServiceAccount.toLowerCase() === 'epadmin';
    if (aIsEpadmin && !bIsEpadmin) return -1;
    if (!aIsEpadmin && bIsEpadmin) return 1;
    return a.fdsServiceAccount.localeCompare(b.fdsServiceAccount);
  });

  const dataRegionOptions = [
    { value: '', label: 'Use system default' },
    ...DATACENTERS.map((dc) => ({ value: dc.code, label: dc.name })),
  ];

  const serviceAccountOptions = [
    { value: '', label: isLoadingAccounts ? 'Loading...' : 'Use system default' },
    ...sortedAccounts.map((profile) => ({ value: profile.fdsServiceAccount, label: profile.fdsServiceAccount })),
  ];

  return (
    <Popover>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
          aria-label="Settings"
        >
          <AccountSettingIcon className="h-4 w-4" />
        </button>
      </Popover.Trigger>
      <Popover.Content align="end" variant="light" className="w-72 p-4">
        <div className="space-y-4">
          <h4 className="text-base font-semibold my-1">Default Settings</h4>
          <hr className="border-border" />

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <GlobeAsiaIcon className="h-4 w-4 shrink-0" />
              <span>Default Data Region</span>
            </div>
            <CustomSelect
              value={defaults.defaultDataRegion ?? ''}
              onChange={(value) => setDefaultDataRegion(value || undefined)}
              options={dataRegionOptions}
              placeholder="Use system default"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <AccountMultipleIcon className="h-4 w-4 shrink-0" />
              <span>Default Service Account</span>
            </div>
            <CustomSelect
              value={defaults.defaultServiceAccount ?? ''}
              onChange={(value) => setDefaultServiceAccount(value || undefined)}
              options={serviceAccountOptions}
              placeholder="Use system default"
              disabled={isLoadingAccounts}
            />
          </div>

          <hr className="border-border" />
          <p className="text-xs text-muted-foreground">
            These defaults will be used when you first visit the File Delivery pages.
          </p>
        </div>
      </Popover.Content>
    </Popover>
  );
};
